import { ThemeContext } from '@/state/Theme';
import ButtonCircle from '@/components/ButtonCircle';
import { PinIcon } from '@/icons';
import FacebookIcon from '@/icons/FacebookIcon';
import InstagramIcon from '@/icons/InstagramIcon';
import TwitterIcon from '@/icons/TwitterIcon';
import LinkedInIcon from '@/icons/LinkedInIcon';
import { type FooterColumnProps } from './FooterColumn.types';

const FooterColumn: React.FC<FooterColumnProps> = ({
  logoTitle,
  hasContactPage,
  contactUrl,
  footerNav,
  facebookUserName,
  instagramUserName,
  twitterUserName,
  linkedInUserName,
  privacyPolicyPageTitle,
  privacyPolicyPageLink,
  termsOfUsePageTitle,
  termsOfUsePageLink,
  footerUtility1Title,
  footerUtility1Link,
  footerUtility2Title,
  footerUtility2Link,
  copyright,
  googleStoreLink,
  appStoreLink,
}) => (
  <ThemeContext.Consumer>
    {({ brandLogo }) => (
      <footer className="footer footer--top footer--columns">
        <div className="footer__container container">
          <div className="footer__logo-container">
            <a className="footer__logo" href="/" aria-label="Go to Home page">
              {typeof brandLogo === 'string' ? (
                <img src={brandLogo} alt={logoTitle} />
              ) : (
                brandLogo
              )}
            </a>
            {hasContactPage && (
              <a
                className="footer__cta footer__cta--mobile button button--primary"
                href={contactUrl}
              >
                Contact us
                <ButtonCircle
                  arrowHeight={17}
                  arrowWidth={13}
                  customIcon={<PinIcon />}
                />
              </a>
            )}
          </div>

          <div className="footer__links">
            {footerNav.map((page, index) => {
              const { title, link, children } = page;

              return (
                <ul className="footer__list" key={index}>
                  <li className="footer__list-item">
                    <a
                      className="footer__list-link footer__list-link--first h5"
                      href={link}
                    >
                      {title}
                    </a>
                  </li>
                  {children &&
                    children.map((child, i) => {
                      const { title, link } = child;

                      return (
                        <li className="footer__list-item" key={i}>
                          <a className="footer__list-link h6" href={link}>
                            {title}
                          </a>
                        </li>
                      );
                    })}
                </ul>
              );
            })}
          </div>

          <div className="footer__cta-container">
            {hasContactPage && (
              <a
                className="footer__cta button button--primary"
                href={contactUrl}
              >
                Contact us
                <ButtonCircle
                  arrowHeight={13}
                  arrowWidth={17}
                  customIcon={<PinIcon />}
                />
              </a>
            )}
            {(facebookUserName ||
              instagramUserName ||
              twitterUserName ||
              linkedInUserName) && (
              <ul className="footer__socials">
                {facebookUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://facebook.com/${facebookUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Go to Facebook page"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <FacebookIcon
                            width={30}
                            height={30}
                            fill={theme.footerIconColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
                {instagramUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://www.instagram.com/${instagramUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label="Go to Instagram page"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <InstagramIcon
                            width={30}
                            height={30}
                            fill={theme.footerIconColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
                {twitterUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://twitter.com/${twitterUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      aria-label="Go to Twitter page"
                      rel="noreferrer"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <TwitterIcon
                            width={30}
                            height={30}
                            fill={theme.footerIconColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
                {linkedInUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://linkedin.com/company/${linkedInUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      aria-label="Go to Linkedin page"
                      rel="noreferrer"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <LinkedInIcon
                            width={30}
                            height={30}
                            fill={theme.footerIconColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
              </ul>
            )}
            <div className="footer__app-links">
              {googleStoreLink && (
                <a href={googleStoreLink} target="_blank">
                  <img
                    src="/_resources/app/client/img/google-play.png"
                    className="footer__app-links--image"
                  />
                </a>
              )}
              {appStoreLink && (
                <a href={appStoreLink} target="_blank">
                  <img
                    src="/_resources/app/client/img/app-store-dark.svg"
                    className="footer__app-links--image"
                  />
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="footer--bottom footer--columns">
          <div className="footer__container container">
            {(privacyPolicyPageTitle ||
              termsOfUsePageTitle ||
              footerUtility1Title ||
              footerUtility2Title) && (
              <ul className="footer__utility">
                {privacyPolicyPageTitle && (
                  <li className="footer__utility-item">
                    <a
                      href={privacyPolicyPageLink}
                      className="footer__utility-link"
                    >
                      {privacyPolicyPageTitle}
                    </a>
                  </li>
                )}
                {termsOfUsePageTitle && (
                  <li className="footer__utility-item">
                    <a
                      href={termsOfUsePageLink}
                      className="footer__utility-link"
                    >
                      {termsOfUsePageTitle}
                    </a>
                  </li>
                )}
                {footerUtility1Title && (
                  <li className="footer__utility-item">
                    <a
                      href={footerUtility1Link}
                      className="footer__utility-link"
                    >
                      {footerUtility1Title}
                    </a>
                  </li>
                )}
                {footerUtility2Title && (
                  <li className="footer__utility-item">
                    <a
                      href={footerUtility2Link}
                      className="footer__utility-link"
                    >
                      {footerUtility2Title}
                    </a>
                  </li>
                )}
              </ul>
            )}
            <div className="footer__bottom-left">
              <img
                alt="shielded"
                className="footer__shield"
                id="shielded-logo"
                src="https://shielded.co.nz/img/custom-logo.png"
                height="32"
                width="32"
              />
              {copyright && (
                <span className="footer__copyright">{copyright}</span>
              )}
            </div>
          </div>
        </div>
      </footer>
    )}
  </ThemeContext.Consumer>
);

export default FooterColumn;
